// src/editorUtils.js
// 从 EditorPage.js 提取的纯工具函数 & 小组件
import React, { useState } from 'react';

// --- 右键菜单项组件 ---
export const ContextMenuItem = ({ icon, label, shortcut, onClick, danger, disabled }) => {
  const [hover, setHover] = useState(false);
  // 📱 iPad：触摸场景下 onClick(合成点击)不可靠——手指微动、长按 touchend 被 preventDefault、
  //   或 ~300ms 延迟都会让它丢失，表现为"点了没反应、时好时坏"。这里直接在 touchend 上执行，
  //   一抬手立即触发；并用 justTouchedRef 去重，挡掉紧随其后的合成 click 造成的二次触发。
  const justTouchedRef = React.useRef(false);
  const fire = (e) => {
    e.stopPropagation();
    if (!disabled) onClick();
  };
  return (
    <div
      onTouchEnd={(e) => {
        // touchend 直接执行（避开合成 click 的各种丢失），并标记去重
        e.preventDefault();
        justTouchedRef.current = true;
        setTimeout(() => { justTouchedRef.current = false; }, 500);
        fire(e);
      }}
      onClick={(e) => { if (justTouchedRef.current) { e.stopPropagation(); return; } fire(e); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', 
        padding: '8px 12px', borderRadius: '6px', 
        cursor: disabled ? 'default' : 'pointer', 
        opacity: disabled ? 0.35 : 1,
        color: danger ? '#ff4d4f' : (hover && !disabled ? 'var(--text-hover)' : 'var(--context-menu-text)'),
        fontSize: '13px',
        transition: 'all 0.1s',
        background: hover && !disabled ? (danger ? 'rgba(255, 77, 79, 0.1)' : 'var(--bg-hover)') : 'transparent'
      }}
    >
      <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
        {React.cloneElement(icon, { size: 14 })}
        <span>{label}</span>
      </div>
      {shortcut && <span style={{fontSize:'10px', color:'#666', fontFamily:'monospace'}}>{shortcut}</span>}
    </div>
  );
};

// --- 随机文本背景色 ---
const TEXT_BG_COLORS = ['#1e293b', '#2a2a2a', '#2b211e', '#2C2520', '#272e2b', '#2c2a3e', '#4a5568', '#334155'];
export const getRandomTextBg = () => {
  return TEXT_BG_COLORS[Math.floor(Math.random() * TEXT_BG_COLORS.length)];
};

// --- 标题截断 ---
export const formatShortTitle = (title) => {
  if (!title) return "未命名";
  const cleanTitle = title.replace(/\.[^/.]+$/, "");
  return cleanTitle.length > 6 ? cleanTitle.substring(0, 6) + "..." : cleanTitle;
};

// --- 资产数据规范化 ---
export const normalizeAsset = (asset) => {
  if (!asset) return asset;
  const data = asset.data || {};
  const isEncrypted = !!(asset.isEncrypted ?? asset.is_encrypted ?? asset.isSystem ?? asset.is_system);
  const isGlobal = !!(asset.isGlobal ?? asset.is_global);
  return {
    ...asset,
    thumbnailUrl: asset.thumbnailUrl || asset.thumbnail_url || data.thumbnailUrl || data.thumbnail_url,
    miniThumbnailUrl: asset.miniThumbnailUrl || asset.mini_thumbnail_url || data.miniThumbnailUrl || data.mini_thumbnail_url,
    description: asset.description ?? data.description, // 🔧 描述词字段映射
    bgColor: asset.bgColor ?? data.bgColor,
    text: asset.text ?? data.text,
    // 🔧 宽高字段映射：兼容 img_width/img_height 和 data.width/data.height
    width: asset.width ?? asset.img_width ?? data.width,
    height: asset.height ?? asset.img_height ?? data.height,
    isGlobal,
    isEncrypted,
    // 兼容旧逻辑：isSystem 继续映射到加密状态
    isSystem: isEncrypted,
  };
};

// --- 引用图片 URL 选取工具 ---
// 持久 URL：只认 http(s)，拒绝 blob:/local-cache:///data: —— 用于要存库、要跨设备加载的字段
//   （blob/data 换设备即失效；local-cache:// 在本地无缓存时无网络兜底会永久空白）
export const pickDurableUrl = (...cands) =>
  cands.find(u => typeof u === 'string' && /^https?:\/\//.test(u)) || null;
// 内存 blob：只认 blob:，否则 undefined —— 用于 localCacheUrl 快显字段（渲染公式 src = localCacheUrl || url）
export const pickBlobUrl = (u) => (typeof u === 'string' && u.startsWith('blob:') ? u : undefined);

// --- 胶卷图标 ---
export const IconFilm = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" style={{ display: 'block', color }}>
    <path fill="currentColor" d="M232 212h-36.2a107.94 107.94 0 1 0-67.8 24h104a12 12 0 0 0 0-24M44 128a84 84 0 1 1 84 84a84.09 84.09 0 0 1-84-84m64-44a20 20 0 1 1 20 20a20 20 0 0 1-20-20m20 108a20 20 0 1 1 20-20a20 20 0 0 1-20 20m24-64a20 20 0 1 1 20 20a20 20 0 0 1-20-20m-48 0a20 20 0 1 1-20-20a20 20 0 0 1 20 20"/>
  </svg>
);

// --- 尺寸计算工具函数 ---
// 节点最小横向宽度：保证顶部一行容得下「左侧[分辨率]宽×高[体积]条 + 数量角标 + 剥离/关闭按钮」，
// 不被细高图（宽<高）挤压换行或重叠。细高图按长边算出的宽度若低于此值，强制拉到此宽度。
export const MIN_NODE_WIDTH = 220;

export const calculateLongSide = (w, h, targetShortSide) => {
  if (!w || !h) return { width: targetShortSide, height: targetShortSide * 0.8 };
  const ratio = w / h;
  // 短边固定逻辑：横屏固定高度（短边），竖屏固定宽度（短边）
  let size = (w >= h)
    ? { width: Math.round(targetShortSide * ratio), height: targetShortSide }
    : { width: targetShortSide, height: Math.round(targetShortSide / ratio) };
  // 横向宽度下限：只对大节点（targetShortSide >= 240）应用，小引用节点（60px）不受限制
  if (targetShortSide >= 240 && size.width < MIN_NODE_WIDTH) {
    size = { width: MIN_NODE_WIDTH, height: MIN_NODE_WIDTH / ratio };
  }
  return size;
};

export const calculateLongSide500 = (w, h) => calculateLongSide(w, h, 380);
export const calculateLongSide120 = (w, h) => calculateLongSide(w, h, 240);
export const calculateLongSide60 = (w, h) => calculateLongSide(w, h, 60);
export const calculateLongSide90 = (w, h) => calculateLongSide(w, h, 90);
export const calculateLongSideCustom = (w, h, shortSide) => calculateLongSide(w, h, shortSide);
